import type * as Sentry from '@sentry/react-native';

// react-dom 19.2.3 lane bits that count toward the nested-update limit behind
// "Maximum update depth exceeded" (#185): SyncLane | InputContinuousLane |
// DefaultLane. The counter only resets after a commit that leaves none of these
// pending, so an update stuck in one of them makes every later commit count
// until an unrelated setState throws (GROKIO-APP-72). Re-check on React upgrades.
const UPDATE_LANES = 0b101010;
const SAMPLE_INTERVAL_MS = 1000;
const MAX_HOLDERS = 5;
const MAX_PATH_NAMES = 12;
const MAX_TAG_LENGTH = 200;
const MAX_VISITED_FIBERS = 50_000;

const SUSPENSE_TAG = 13;
const OFFSCREEN_TAG = 22;

interface IFiber {
  tag: number;
  type: unknown;
  lanes: number;
  childLanes: number;
  memoizedState: unknown;
  child: IFiber | null;
  sibling: IFiber | null;
  return: IFiber | null;
}

interface IFiberRoot {
  current: IFiber;
  pendingLanes: number;
  suspendedLanes: number;
  pingedLanes: number;
  expiredLanes: number;
  cancelPendingCommit: unknown;
}

interface INamedType {
  displayName?: string;
  name?: string;
  render?: { displayName?: string; name?: string };
  type?: { displayName?: string; name?: string };
}

let fiberRoot: IFiberRoot | null = null;
let pendingSinceMs: number | null = null;
let consecutivePendingSamples = 0;
let samplerId: ReturnType<typeof setInterval> | null = null;

const findFiberRoot = (): IFiberRoot | null => {
  if (fiberRoot) return fiberRoot;
  if (typeof document === 'undefined') return null;

  const candidates = [
    document.getElementById('root'),
    ...Array.from(document.body?.children ?? []),
  ];
  for (const element of candidates) {
    if (!element) continue;
    const containerKey = Object.keys(element).find((key) => key.startsWith('__reactContainer$'));
    const root = containerKey
      ? (element as unknown as Record<string, { stateNode?: IFiberRoot }>)[containerKey]?.stateNode
      : undefined;
    if (root?.current) {
      fiberRoot = root;
      return root;
    }
  }
  return null;
};

const toBinary = (lanes: number) => lanes.toString(2);

const getFiberName = (fiber: IFiber): string | null => {
  if (fiber.tag === SUSPENSE_TAG) {
    return fiber.memoizedState ? 'Suspense(fallback)' : 'Suspense';
  }
  if (fiber.tag === OFFSCREEN_TAG) {
    return fiber.memoizedState ? 'Offscreen(hidden)' : null;
  }
  const { type } = fiber;
  if (typeof type === 'string') return type;
  if (!type || (typeof type !== 'function' && typeof type !== 'object')) return null;
  const named = type as INamedType;
  return (
    named.displayName ||
    named.render?.displayName ||
    named.type?.displayName ||
    named.name ||
    named.render?.name ||
    named.type?.name ||
    null
  );
};

/** Ancestor names from the root down to the fiber, nearest ancestors kept. */
const describePath = (fiber: IFiber): string => {
  const names: string[] = [];
  for (let node: IFiber | null = fiber; node; node = node.return) {
    const name = getFiberName(node);
    if (name && name !== names[names.length - 1]) names.push(name);
  }
  return names.slice(0, MAX_PATH_NAMES).reverse().join(' > ');
};

/**
 * Fibers holding a pending update in one of the counted lanes. Walks the whole
 * tree: ancestors' childLanes are only marked once React starts rendering the
 * update, so they can't be used to prune the search.
 */
const findUpdateHolders = (root: IFiberRoot): string[] => {
  const holders: string[] = [];
  if (root.current.lanes & UPDATE_LANES) holders.push('HostRoot');

  const stack: IFiber[] = root.current.child ? [root.current.child] : [];
  let visited = 0;
  while (stack.length > 0 && holders.length < MAX_HOLDERS && visited < MAX_VISITED_FIBERS) {
    const fiber = stack.pop()!;
    visited += 1;
    if (fiber.sibling) stack.push(fiber.sibling);
    if (fiber.child) stack.push(fiber.child);
    if (fiber.lanes & UPDATE_LANES) holders.push(describePath(fiber));
  }
  return holders;
};

const samplePendingLanes = () => {
  const root = findFiberRoot();
  if (!root) return;
  if (root.pendingLanes & UPDATE_LANES) {
    pendingSinceMs ??= Date.now();
    consecutivePendingSamples += 1;
  } else {
    pendingSinceMs = null;
    consecutivePendingSamples = 0;
  }
};

const getPendingForBucket = (pendingForMs: number | null): string => {
  if (pendingForMs === null) return 'not-sampled';
  if (pendingForMs < 10_000) return '<10s';
  if (pendingForMs < 60_000) return '10-60s';
  if (pendingForMs < 600_000) return '1-10min';
  return '>10min';
};

const isUpdateDepthError = (event: Sentry.ErrorEvent): boolean =>
  event.exception?.values?.some(
    (exception) =>
      exception.value?.includes('Maximum update depth exceeded') ||
      exception.value?.includes('Minified React error #185')
  ) ?? false;

/**
 * Samples the React root once a second so a "Maximum update depth exceeded"
 * report can tell how long counted updates have been pending.
 */
export const startReactRootDiagnostics = (): void => {
  if (samplerId !== null || typeof window === 'undefined') return;
  samplerId = setInterval(samplePendingLanes, SAMPLE_INTERVAL_MS);
};

/**
 * Attaches the React root's pending-update state to "Maximum update depth
 * exceeded" events: which counted lanes are pending, whether React parked them
 * as suspended, for how long, and the component paths holding them.
 */
export const addReactRootDiagnostics = (event: Sentry.ErrorEvent): void => {
  if (!isUpdateDepthError(event)) return;

  const root = findFiberRoot();
  if (!root) {
    event.tags = { ...event.tags, 'react185.root': 'missing' };
    return;
  }

  const pendingLanes = root.pendingLanes & UPDATE_LANES;
  const suspendedLanes = pendingLanes & root.suspendedLanes;
  const pendingForMs = pendingLanes && pendingSinceMs !== null ? Date.now() - pendingSinceMs : null;
  const holders = findUpdateHolders(root);

  event.tags = {
    ...event.tags,
    'react185.pending': pendingLanes ? toBinary(pendingLanes) : 'none',
    'react185.suspended': suspendedLanes ? 'yes' : 'no',
    'react185.pending_for': pendingLanes ? getPendingForBucket(pendingForMs) : 'none',
    'react185.holder': (holders[0] ?? 'none').slice(-MAX_TAG_LENGTH),
  };
  event.contexts = {
    ...event.contexts,
    react_root: {
      pendingLanes: toBinary(root.pendingLanes),
      suspendedLanes: toBinary(root.suspendedLanes),
      pingedLanes: toBinary(root.pingedLanes),
      expiredLanes: toBinary(root.expiredLanes),
      commitSuspended: Boolean(root.cancelPendingCommit),
      pendingForMs,
      consecutivePendingSamples,
      holders,
    },
  };
};
